import {X} from 'lucide-react'
import {useCallback, KeyboardEvent, forwardRef} from 'react'
import {cn} from '@/lib/utils'

export interface FilterChipProps {
    /** Human-readable filter name, e.g. "Status". */
    label: string
    /**
     * One or more values joined for display. Caller chooses the formatting —
     * the chip renders the string as-is and truncates mid-sentence with CSS.
     */
    value: string
    /**
     * Required — the bar passes the raw values separately so the aria-label
     * can include them verbatim for screen readers.
     */
    values: ReadonlyArray<string>
    /**
     * Caller-provided accessible label. When omitted, defaults to the English
     * pattern `Remove filter: {label} {values}`. Callers that live inside an
     * i18n tree pass the localised form via the `useI18n()` proxy.
     */
    ariaLabel?: string
    /** Fires on click-×, Enter, Space, Delete or Backspace. */
    onRemove: () => void
    /**
     * Width cap — desktop default 240px, narrow default 160px (§5.3 i18n
     * crowding rule). Callers compose the responsive class; the component
     * stays viewport-agnostic.
     */
    maxWidthClass?: string
    /**
     * Rendering mode. `labeled` (default) shows `{label}: {value}` — the
     * standard shape for filters where the field name is needed for context
     * (e.g. "Status: Active", "Type: Person, Legal Entity").
     *
     * `selfEvident` shows `{value}` alone — used for Boolean filters whose
     * per-field labels carry the semantic on their own ("Active" means
     * `active=true`, "Inactive" means `active=false`). Feedback made this
     * explicit after chips were reading "Active: true" — which is
     * engineering-speak, not user-facing language.
     */
    mode?: 'labeled' | 'selfEvident'
    testId?: string
}

/**
 * A focusable filter pill with × removal. Keyboard: Enter, Space, Delete,
 * or Backspace removes the filter. ARIA: role="button" + descriptive
 * aria-label per §5.3. Visual × is a decoration inside the chip, not a
 * separate focusable element — this keeps the tab order manageable when
 * many chips are present (§5.3 chip interaction rules).
 */
export const FilterChip = forwardRef<HTMLButtonElement, FilterChipProps>(function FilterChip(

    {label, value, values, ariaLabel, onRemove, maxWidthClass = 'max-w-[240px] sm:max-w-[240px]', mode = 'labeled', testId},
    ref,
) {
    const handleKeyDown = useCallback((e: KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === 'Delete' || e.key === 'Backspace' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onRemove()
        }
    }, [onRemove])


    const valuesLine = values.join(', ')
    // eslint-disable-next-line i18next/no-literal-string -- fallback English label; caller normally supplies localised one
    const resolvedAriaLabel = ariaLabel ?? `Remove filter: ${label} ${valuesLine}`
    const tooltipTitle = mode === 'selfEvident' ? value : `${label}: ${value}`

    return (
        <button
            ref={ref}
            type="button"
            role="button"
            aria-label={resolvedAriaLabel}
            title={tooltipTitle}
            data-testid={testId}
            onClick={onRemove}
            onKeyDown={handleKeyDown}
            className={cn(
                'inline-flex items-center gap-1 rounded-full border border-input bg-muted px-2.5 py-0.5 text-xs font-medium',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                'hover:bg-accent cursor-pointer',
                maxWidthClass,
            )}
        >
            <span className="truncate">
                {mode === 'selfEvident' ? (
                    <span className="font-medium">{value}</span>
                ) : (
                    <>
                        <span className="font-semibold">{label}:</span>{' '}
                        <span className="font-normal">{value}</span>
                    </>
                )}
            </span>
            <X className="h-3 w-3 shrink-0 opacity-70" aria-hidden="true"/>
        </button>
    )
})
